// Shared enums, literal texts (§11) and client-side derivations (§5) for the advisor panel.

export const CRIBADO_KEYS = [
  'p1_profundidad_ejecutiva', 'p2_encaje_pyme', 'p3_especificidad_criterio',
  'p4_transferibilidad', 'p5_compromiso', 'p6_verificabilidad',
];
export const VERTICAL_KEYS = ['v1_relevo', 'v2_internacionalizacion', 'v3_financiera', 'v4_crecimiento'];
export const ROLE_KEYS = ['r1', 'r2', 'r3', 'r4', 'r5', 'r6'];
export const EXCLUSION_KEYS = [
  'A1_no_verificable', 'A2_sin_responsabilidad_ejecutiva', 'A3_canal_de_venta',
  'A4_disponibilidad_nula', 'A5_rechaza_modelo', 'A6_conflicto_irresoluble',
  'A7_incidencia_reputacional', 'A8_fuera_de_cobertura',
];
export const EXCLUSION_LABELS = {
  A1_no_verificable: 'A1 · No verificable',
  A2_sin_responsabilidad_ejecutiva: 'A2 · Sin responsabilidad ejecutiva',
  A3_canal_de_venta: 'A3 · Canal de venta',
  A4_disponibilidad_nula: 'A4 · Disponibilidad nula',
  A5_rechaza_modelo: 'A5 · Rechaza el modelo',
  A6_conflicto_irresoluble: 'A6 · Conflicto irresoluble',
  A7_incidencia_reputacional: 'A7 · Incidencia reputacional',
  A8_fuera_de_cobertura: 'A8 · Fuera de cobertura',
};

// Role labels: only r1 (Dirección general) and r6 (Tecnología) are fixed by the spec.
// r2–r5 are provisional (C-suite standard) and editable.
export const ROLE_LABELS = {
  r1: 'R1 · Dirección general', r2: 'R2 · Financiera', r3: 'R3 · Comercial',
  r4: 'R4 · Operaciones', r5: 'R5 · Personas/RRHH', r6: 'R6 · Tecnología',
};
export const VERTICAL_LABELS = {
  v1_relevo: 'V1 · Relevo y gobierno familiar',
  v2_internacionalizacion: 'V2 · Internacionalización',
  v3_financiera: 'V3 · Dirección financiera externa',
  v4_crecimiento: 'V4 · Crecimiento y estrategia',
};
export const VERTICAL_PREGUNTAS = {
  v1_relevo: 'En el relevo que vivió, ¿qué pasó con el familiar que esperaba un puesto y no encajaba?',
  v2_internacionalizacion: '¿Qué mercado abrió que salió mal, y en qué momento supieron que había que salir?',
  v3_financiera: 'Cuénteme una tensión de tesorería real: ¿en qué orden pagó y a quién dejó para después?',
  v4_crecimiento: 'Para crecer, ¿qué dejaron de hacer? ¿Qué cliente, producto o canal sacrificaron?',
};
export const VERTICAL_OPTIONS = ['Acreditada', 'Adyacente', 'No'];
export const ROLE_OPTIONS = ['Referencia', 'Solido', 'Adyacente', 'No'];
export const ESCALAS = ['<10', '10-50', '50-250', '250-1000', '>1000'];
export const ETAPAS = ['Fundacion/arranque', 'Crecimiento', 'Consolidacion', 'Profesionalizacion', 'Crisis o reestructuracion', 'Sucesion o venta'];
export const SECTORES = ['Industrial/manufacturero', 'Alimentacion', 'Construccion', 'Energia', 'Servicios B2B', 'Distribucion/retail', 'Tecnologia', 'Salud', 'Otro'];
export const NIVELES = ['Consejero (R1)', 'Directivo funcional (R2)', 'Especialista (R3)'];
export const TIPOLOGIAS = ['Estrategico', 'Tecnico hands-on', 'Ambos'];
export const MODELOS = ['Sesion puntual', 'Iguala mensual', 'Proyecto cerrado', 'Consejo asesor'];
export const MODALIDADES = ['Presencial', 'Remoto', 'Hibrido'];
export const RESULTADOS_FINALES = ['Admitido', 'Admitido condicionado', 'En reserva', 'Lista de espera', 'Rechazado'];
export const MOTIVOS_RECHAZO = ['R-VER', 'R-SEN', 'R-PYM', 'R-FIR', 'R-DIS', 'R-MOD', 'R-CON', 'R-COB', 'R-CAL'];

// §11 · Anclas de puntuación (0–4)
export const ANCLAS = {
  p1_profundidad_ejecutiva: {
    title: 'P1 · Profundidad ejecutiva verificada',
    scale: [
      '0 · solo funciones de asesoría o staff',
      '1 · mando intermedio con equipo, sin responsabilidad sobre resultado',
      '2 · dirección funcional con P&L de su área',
      '3 · dirección general, comité de dirección o impacto en el P&L global',
      '4 · primer ejecutivo o consejero con responsabilidad plena, en más de una compañía',
    ],
    nota: 'Solo puntúa lo verificado: un cargo declarado y no confirmado puntúa como si no existiera.',
  },
  p2_encaje_pyme: {
    title: 'P2 · Encaje con la escala PYME',
    scale: [
      '0 · solo gran corporación',
      '1 · solo gran corporación, pero acredita comprensión del contexto PYME',
      '2 · ha trabajado con PYMES como proveedor, consultor o inversor',
      '3 · responsabilidad ejecutiva en empresa de 10-250 empleados',
      '4 · trayectoria mayoritaria ahí, incluida empresa familiar',
    ],
  },
  p3_especificidad_criterio: {
    title: 'P3 · Especificidad del criterio',
    scale: [
      '0 · lugares comunes',
      '1 · concreto en el qué, ausente el cómo y el coste',
      '2 · contexto, decisión y resultado identificables',
      '3 · se ve el dilema y qué se sacrificó',
      '4 · además, consecuencias no deseadas o aprendizaje contraintuitivo',
    ],
    nota: 'Señal de alarma: si solo hay éxitos limpios, baja un punto.',
  },
  p4_transferibilidad: {
    title: 'P4 · Transferibilidad',
    scale: [
      '0 · solo se ve ejecutando',
      '1 · asesoría anecdótica',
      '2 · ha mentorizado o asesorado de forma recurrente',
      '3 · consejo asesor o de administración con continuidad',
      '4 · además, adapta el mensaje a un interlocutor no experto',
    ],
  },
  p5_compromiso: {
    title: 'P5 · Compromiso operativo real',
    scale: [
      '0 · menos de 4 h/mes',
      '1 · de 4 a 8 h/mes con reservas',
      '2 · de 8 a 16 h/mes con franjas concretas',
      '3 · además, respuesta ágil durante el alta',
      '4 · además, acepta reunión de encaje sin garantía de mandato',
    ],
  },
  p6_verificabilidad: {
    title: 'P6 · Verificabilidad e higiene del perfil',
    scale: [
      '0 · perfil opaco o incoherente',
      '1 · verificable con esfuerzo, con lagunas',
      '2 · coherente y verificable en lo esencial',
      '3 · además, rastro público profesional',
      '4 · además, respaldo de terceros identificables',
    ],
  },
};

export const A3_CONTROL = '"Si una empresa le contratara mañana para esto, ¿quién haría el trabajo?" Si la respuesta incluye a un equipo, una metodología propietaria o una propuesta por fases, es A3.';
export const ACTIVO_HELP = 'Si tuvieras que explicarle a un CEO en diez segundos por qué esta persona y no otra, ¿qué dirías? Ejemplo: «Ha pilotado dos sucesiones familiares en industria donde el hijo no valía, y sabe llevar esa conversación sin romper la familia.»';

// §5 client-side derivation for the live side panel
export function computeDerived(c, config) {
  const pesos = config.pesos;
  const ps = CRIBADO_KEYS.map((k) => c[k]);
  let fit = null;
  if (ps.every((p) => p !== null && p !== undefined)) {
    const w = [pesos.p1, pesos.p2, pesos.p3, pesos.p4, pesos.p5, pesos.p6];
    fit = Math.round((ps.reduce((a, p, i) => a + p * w[i], 0) / 4) * 10) / 10;
  }
  const verticales_acreditadas = VERTICAL_KEYS.filter((k) => c[k] === 'Acreditada').length;
  const roles_referencia = ROLE_KEYS.filter((k) => c[k] === 'Referencia').length;
  const roles_utiles = ROLE_KEYS.filter((k) => c[k] === 'Referencia' || c[k] === 'Solido').length;
  const excluido = EXCLUSION_KEYS.some((k) => !!c[k]);

  const p1 = c.p1_profundidad_ejecutiva;
  let nivel_sugerido = null;
  if (p1 === 3 || p1 === 4) nivel_sugerido = 'Consejero (R1)';
  else if (p1 === 2) nivel_sugerido = 'Directivo funcional (R2)';
  else if (p1 !== null && p1 !== undefined && p1 < 2) nivel_sugerido = 'No admisible';

  let resultado_sugerido;
  if (!c.nombre) resultado_sugerido = '';
  else if (excluido) resultado_sugerido = 'Rechazado';
  else if (fit === null) resultado_sugerido = 'Sin puntuar';
  else if (fit < config.umbral_condicionado) resultado_sugerido = 'Rechazado';
  else if (fit < config.umbral_admision) resultado_sugerido = 'Admitido condicionado';
  else if (verticales_acreditadas >= 1 && roles_utiles >= 1) resultado_sugerido = 'Admitido';
  else resultado_sugerido = 'Lista de espera';

  return { fit, verticales_acreditadas, roles_referencia, roles_utiles, excluido, nivel_sugerido, resultado_sugerido };
}
